/**
 * Two-tier subscription model (file 05 §249-276).
 *
 *   Tier 'domain'      Wildcard / aggregate; refcounted by `domain`.
 *                      Lifecycle gate: domain refcount > 0.
 *                      Examples: morfi.market.*.state_change · morfi.summary.markets.tick
 *
 *   Tier 'per-entity'  Entity-scoped; viewport-gated when `viewportGated: true`.
 *                      Lifecycle gate: domain mounted AND consumer in viewport.
 *                      Examples: morfi.market.{id}.trade · morfi.market.{id}.boost
 *
 * The discriminant lets codegen + lint refuse a `RoutingEntry` whose subject
 * pattern's classified tier doesn't match its declared `domainPins` vs
 * `perEntitySubjects` placement. A `domainPins` entry pointing at a literal
 * `.{id}.trade` subject is a bug (per-entity subject leaked into the
 * always-on aggregate slot — opens N subscriptions instead of 1).
 */
export type SubscriptionTier = 'domain' | 'per-entity';

/**
 * Heuristic classifier: a subject literal containing a wildcard token
 * (`*` or `>`) is a domain tier; a subject containing only literal
 * segments is per-entity. Used by codegen + lint, not at runtime.
 *
 * Note: literal aggregate subjects (e.g. `morfi.summary.markets.tick`)
 * classify as `'per-entity'` here because the heuristic is purely
 * lexical — they are still refcounted via `domain` because they appear
 * in a spec's `domainPins` array, not a `perEntitySubjects` callback.
 * The classifier is a lint aid, not a runtime gate.
 */
export function classifyTier(subjectPattern: string): SubscriptionTier {
  return subjectPattern.includes('*') || subjectPattern.includes('>') ? 'domain' : 'per-entity';
}
